import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import servicesService from "../services/servicesService";
import styles from "../assets/services.module.css";

const Services = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newServiceName, setNewServiceName] = useState("");
  const [formError, setFormError] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editError, setEditError] = useState("");

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    setLoading(true);
    try {
      const response = await servicesService.getAllServices();
      console.log("🔍 Services API Response:", response);
      
      if (response.success && Array.isArray(response.data)) {
        console.log("✅ Using response.data:", response.data.length, "services");
        setServices(response.data.sort((a, b) => a.name.localeCompare(b.name)));
      } else if (Array.isArray(response)) {
        console.log("✅ Using direct array:", response.length, "services");
        setServices(response.sort((a, b) => a.name.localeCompare(b.name)));
      } else {
        console.error("❌ Unexpected API response structure:", response);
        setServices([]);
      }
    } catch (error) {
      console.error("❌ Error loading services:", error);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddService = async (e) => {
    e.preventDefault();

    if (!newServiceName.trim()) {
      setFormError("Service name is required");
      return;
    }

    setFormError("");

    try {
      await servicesService.createService({ name: newServiceName.trim() });
      toast.success("Service created successfully!");
      setNewServiceName("");
      setShowAddModal(false);
      loadServices(); // Reload services to show the new one
    } catch (error) {
      console.error("Error creating service:", error);
      toast.error("Failed to create service. Please try again.");
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm("Are you sure you want to remove?")) {
      return;
    }

    try {
      await servicesService.deleteService(serviceId);
      toast.success("Service successfully deleted");
      loadServices(); // Reload services
    } catch (error) {
      console.error("Error deleting service:", error);
      toast.error("Failed to delete service. Please try again.");
    }
  };

  const handleVisibilityChange = async (serviceId, visible) => {
    try {
      await servicesService.updateServiceVisibility(serviceId, visible);
      // Update local state immediately for better UX
      setServices((prevServices) =>
        prevServices.map((service) =>
          service.id === serviceId
            ? { ...service, visible_to_guardians: visible }
            : service
        )
      );
      toast.success("Service saved!");
    } catch (error) {
      console.error("Error updating service visibility:", error);
      toast.error("Failed to update service visibility. Please try again.");
      // Revert the change on error
      setServices((prevServices) =>
        prevServices.map((service) =>
          service.id === serviceId
            ? { ...service, visible_to_guardians: !visible }
            : service
        )
      );
    }
  };

  const handleEditStart = (service) => {
    setEditingId(service.id);
    setEditName(service.name);
    setEditError("");
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditName("");
    setEditError("");
  };

  const handleEditSubmit = async (serviceId) => {
    const trimmed = editName.trim();

    if (!trimmed) {
      setEditError("Service name is required");
      return;
    }

    if (trimmed.length > 255) {
      setEditError("Service name must not exceed 255 characters");
      return;
    }

    // Check for duplicate name locally
    const duplicate = services.find(
      (s) => s.id !== serviceId && s.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (duplicate) {
      setEditError("Service name already exists");
      return;
    }

    try {
      await servicesService.updateServiceName(serviceId, trimmed);
      setServices((prev) =>
        prev.map((s) => (s.id === serviceId ? { ...s, name: trimmed } : s))
      );
      toast.success("Service name updated successfully!");
      handleEditCancel();
    } catch (error) {
      console.error("Error updating service name:", error);
      const msg = error.response?.data?.message || "Failed to update service name.";
      setEditError(msg);
    }
  };

  const handleEditKeyDown = (e, serviceId) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleEditSubmit(serviceId);
    } else if (e.key === "Escape") {
      handleEditCancel();
    }
  };

  const isGuardService = (serviceId) => {
    // Check if this is a guard service that cannot be deleted
    // This would typically come from config, but for now we'll hardcode some IDs
    const guardServiceIds = [1, 2]; // Example guard service IDs
    return guardServiceIds.includes(serviceId);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return "⇅";
    }
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  const getSortedServices = () => {
    let sortedServices = [...services];

    if (sortConfig.key) {
      sortedServices.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle different data types
        if (sortConfig.key === "visible_to_guardians") {
          aValue = aValue ? 1 : 0;
          bValue = bValue ? 1 : 0;
        } else if (typeof aValue === "string") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return sortedServices;
  };

  if (loading) {
    return (
      <div className={styles.servicesContainer}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div className={styles.servicesContainer}>
      {/* Breadcrumb */}

      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
          onClick={() => navigate('/dashboard')}
        >
          Dashboard
        </span>
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
          onClick={() => navigate('/settings')}
        >
          / Menu
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Services</span>
        </div>
      </div>

      {/* Main Panel */}
      <div className={styles.panelStandard}>
        <div className={styles.panelBody}>
          <div className={styles.panelHeading}>
            <div className={styles.panelTitle}>
              <h3>Services</h3>
              <button
                className={styles.addServiceButton}
                onClick={() => setShowAddModal(true)}
              >
                <i className="fa fa-plus"></i> Add Service
              </button>
            </div>
          </div>
          <div style={{ paddingLeft: "30%", paddingRight: "30%" }}>
            {formError && (
              <div
                className={styles.errorMessage}
                style={{ textAlign: "center" }}
              >
                {formError}
              </div>
            )}

            <table className={styles.servicesTable}>
              <thead>
                <tr>
                  <th
                    className={styles.nameColumn}
                    onClick={() => handleSort("name")}
                    style={{ cursor: "pointer" }}
                  >
                    Name <span style={{ marginLeft: "4px" }}>{getSortIcon("name")}</span>
                  </th>
                  <th
                    className={styles.visibilityColumn}
                    onClick={() => handleSort("visible_to_guardians")}
                    style={{ cursor: "pointer" }}
                  >
                    Visible for guardians <span style={{ marginLeft: "4px" }}>{getSortIcon("visible_to_guardians")}</span>
                  </th>
                  <th className={styles.actionColumn}>Action</th>
                </tr>
              </thead>
              <tbody>
                {getSortedServices().map((service) => (
                  <tr key={service.id}>
                    <td>
                      {editingId === service.id ? (
                        <div className={styles.editNameWrapper}>
                          <div className={styles.editNameRow}>
                            <input
                              type="text"
                              className={`${styles.editNameInput} ${editError ? styles.error : ""}`}
                              value={editName}
                              onChange={(e) => {
                                setEditName(e.target.value);
                                setEditError("");
                              }}
                              onKeyDown={(e) => handleEditKeyDown(e, service.id)}
                              autoFocus
                            />
                            <button
                              className={styles.editSaveButton}
                              onClick={() => handleEditSubmit(service.id)}
                              title="Save"
                            >
                              &#10003;
                            </button>
                            <button
                              className={styles.editCancelButton}
                              onClick={handleEditCancel}
                              title="Cancel"
                            >
                              &#10005;
                            </button>
                          </div>
                          {editError && (
                            <div className={styles.errorMessage}>{editError}</div>
                          )}
                        </div>
                      ) : (
                        <span
                          className={styles.editableName}
                          onClick={() => handleEditStart(service)}
                          title="Click to edit"
                        >
                          {service.name}
                        </span>
                      )}
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        className={styles.visibilityCheckbox}
                        checked={service.visible_to_guardians}
                        onChange={(e) =>
                          handleVisibilityChange(service.id, e.target.checked)
                        }
                      />
                    </td>
                    <td>
                      {!isGuardService(service.id) && (
                        <button
                          className={styles.deleteButton}
                          onClick={() => handleDeleteService(service.id)}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add Service Modal */}
      {showAddModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowAddModal(false)}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h4>Add service</h4>
              <button
                className={styles.closeButton}
                onClick={() => setShowAddModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleAddService} className={styles.modalForm}>
              <div className={styles.modalBody}>
                <div className={styles.formGroup}>
                  <label
                    className={`${styles.formLabel} ${styles.requiredLabel}`}
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    className={`${styles.formInput} ${
                      formError ? styles.error : ""
                    }`}
                    value={newServiceName}
                    onChange={(e) => setNewServiceName(e.target.value)}
                    required
                  />
                  {formError && (
                    <div className={styles.errorMessage}>{formError}</div>
                  )}
                </div>

                <div style={{ textAlign: "center" }}>
                  <button type="submit" className={styles.submitButton}>
                    <i className="fa fa-plus"></i> Add Service
                  </button>
                </div>
              </div>
            </form>

            <div className={styles.modalFooter}>
              <button
                className={styles.closeModalButton}
                onClick={() => setShowAddModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;
